"use strict";

const moduleStyles = new Set();

function loadModuleStyle(href) {
  if (!href) {
    return Promise.resolve();
  }

  if (moduleStyles.has(href) || document.querySelector(`link[data-module-style="${href}"]`)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.media = "print";
    link.dataset.moduleStyle = href;
    link.onload = () => {
      link.media = "all";
      moduleStyles.add(href);
      resolve();
    };
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

function loadScript(src, check) {
  if (window.themeLoadScript) {
    return window.themeLoadScript(src, check);
  }

  if (typeof check === "function" && check()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || "no_zoom";
    this.zoomOption = this.dataset.zoomOption || "external";
    this.lightbox = null;
    this.lightboxPromise = null;
    this.driftInstances = [];
    this.driftPromise = null;
    this.lightboxTriggersBound = false;
    this.driftTriggersBound = false;
    this.boundResponsive = this.responsive.bind(this);
    this.boundHandleLightboxTrigger = this.handleLightboxTrigger.bind(this);
    this.boundHandleDriftIntent = this.handleDriftIntent.bind(this);
    this.init();
  }

  init() {
    if (this.type === "no_zoom") return;

    window.addEventListener("resize", this.boundResponsive, { passive: true });
    window.addEventListener("load", this.boundResponsive, { once: true });
    this.responsive();
  }

  bindLightboxTriggers() {
    if (this.lightboxTriggersBound) return;

    this.lightboxTriggersBound = true;
    this.querySelectorAll("a.media-gallery__image").forEach((anchor) => {
      anchor.addEventListener("click", this.boundHandleLightboxTrigger);
    });
  }

  bindDriftTriggers() {
    if (this.driftTriggersBound) return;

    this.driftTriggersBound = true;
    this.querySelectorAll(".drift-trigger").forEach((trigger) => {
      trigger.addEventListener("pointerenter", this.boundHandleDriftIntent, { once: true });
      trigger.addEventListener("focusin", this.boundHandleDriftIntent, { once: true });
    });
  }

  getTargetPosition(target) {
    const targetPosition = parseInt(target?.getAttribute("data-position"), 10);

    if (!Number.isNaN(targetPosition) && targetPosition > 0) {
      return targetPosition - 1;
    }

    const gallery = target?.closest("media-gallery");
    const activeSlide = gallery?.querySelector(".swiper-slide-active[data-position]");
    const activePosition = parseInt(activeSlide?.getAttribute("data-position"), 10);

    if (!Number.isNaN(activePosition) && activePosition > 0) {
      return activePosition - 1;
    }

    const firstSlide = gallery?.querySelector("[data-position]");
    const firstPosition = parseInt(firstSlide?.getAttribute("data-position"), 10);

    if (!Number.isNaN(firstPosition) && firstPosition > 0) {
      return firstPosition - 1;
    }

    return 0;
  }

  async handleLightboxTrigger(event) {
    event.preventDefault();

    const lightbox = await this.ensureLightbox();
    if (!lightbox) return;

    const position = this.getTargetPosition(event.currentTarget);
    lightbox.loadAndOpen(position);
  }

  async ensureLightbox() {
    if (this.lightbox) {
      return this.lightbox;
    }

    if (this.lightboxPromise) {
      return this.lightboxPromise;
    }

    this.lightboxPromise = (async () => {
      await loadModuleStyle(new URL("./photoswipe.css", import.meta.url).href);

      const [{ default: PhotoSwipeLightbox }, { default: Photoswipe }] = await Promise.all([
        import("./photoswipe-lightbox.esm.min.js"),
        import("./photoswipe.esm.min.js"),
      ]);

      if (!this.isConnected) {
        return null;
      }

      const lightbox = new PhotoSwipeLightbox({
        gallery: this,
        children: "a.media-gallery__image",
        pswpModule: () => Photoswipe,
        counter: false,
        zoom: false,
        preloader: false,
        arrowPrev: false,
        arrowNext: false,
        close: false,
        loop: false,
        bgOpacity: 1,
      });

      lightbox.on("uiRegister", () => {
        const { pswp } = lightbox;

        pswp?.ui.registerElement({
          name: "bls--close",
          isButton: true,
          tagName: "button",
          html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
          onClick: () => {
            pswp.close();
          },
        });

        pswp?.ui.registerElement({
          name: "bottomBar",
          className: "pswp__bottom-bar",
          appendTo: "wrapper",
          onInit: (el, pswpInstance) => {
            const next = document.createElement("button");
            next.setAttribute("type", "button");
            next.className = "pswp__button pswp__button-next";
            next.innerHTML = `<svg width="6" height="11" fill="none"><use href="#icon-next"></use></svg>`;
            next.onclick = () => {
              pswpInstance.next();
            };
            el.appendChild(next);

            const counter = document.createElement("span");
            counter.className = "pswp__counter";
            pswpInstance.on("change", () => {
              counter.innerText =
                pswpInstance.currIndex +
                1 +
                pswpInstance.options.indexIndicatorSep +
                pswpInstance.getNumItems();
            });
            el.appendChild(counter);

            const prev = document.createElement("button");
            prev.setAttribute("type", "button");
            prev.className = "pswp__button pswp__button-prev";
            prev.innerHTML = `<svg width="6" height="11" fill="none"><use href="#icon-back"></use></svg>`;
            prev.onclick = () => {
              pswpInstance.prev();
            };
            el.appendChild(prev);
          },
        });
      });

      lightbox.init();
      this.lightbox = lightbox;
      return lightbox;
    })().catch((error) => {
      this.lightboxPromise = null;
      throw error;
    });

    return this.lightboxPromise;
  }

  async handleDriftIntent() {
    await this.initDrift();
  }

  async initDrift() {
    if (this.driftInstances.length) {
      return;
    }

    if (this.driftPromise) {
      return this.driftPromise;
    }

    this.driftPromise = (async () => {
      await loadModuleStyle(new URL("./drift-basic.min.css", import.meta.url).href);
      await loadScript(new URL("./drift.js", import.meta.url).href, () => typeof window.Drift !== "undefined");

      if (!this.isConnected) {
        return;
      }

      const paneContainer = this.closest(".sec__featured-product")?.querySelector(".zoom-external-area");
      const Drift = window.Drift;

      this.querySelectorAll(".drift-trigger").forEach((trigger) => {
        const inlineContainer = trigger.closest("[data-pane-container]");
        const zoomOption = this.zoomOption;
        let driftInstance = null;

        if (zoomOption === "inner-2") {
          driftInstance = new Drift(trigger, {
            inlinePane: true,
            zoomFactor: 3,
            containInline: paneContainer,
            paneContainer: inlineContainer,
            hoverBoundingBox: false,
            onShow: () => {
              inlineContainer?.classList.add("relative");
            },
            onHide: () => {
              inlineContainer?.classList.remove("relative");
            },
          });
        } else {
          driftInstance = new Drift(trigger, {
            inlinePane: zoomOption,
            zoomFactor: 5,
            containInline: paneContainer,
            paneContainer: zoomOption === "external" ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === "external",
            onShow: () => {
              inlineContainer?.classList.add("relative");
            },
            onHide: () => {
              inlineContainer?.classList.remove("relative");
            },
          });
        }

        if (driftInstance) {
          this.driftInstances.push(driftInstance);
        }
      });
    })().finally(() => {
      this.driftPromise = null;
    });

    return this.driftPromise;
  }

  destroyLightbox() {
    if (this.lightbox !== null) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
    this.lightboxPromise = null;
  }

  destroyDrift() {
    this.driftInstances.forEach((instance) => {
      instance?.destroy?.();
    });
    this.driftInstances = [];
    this.driftPromise = null;
  }

  responsive() {
    if (window.innerWidth >= 768) {
      if (this.type === "open_lightbox") {
        this.destroyDrift();
        this.bindLightboxTriggers();
      } else {
        this.destroyLightbox();
        if (this.driftTriggersBound) {
          this.initDrift();
        } else {
          this.bindDriftTriggers();
        }
      }
    } else {
      this.destroyDrift();
      this.bindLightboxTriggers();
    }

    this.updateSwiper();
  }

  updateSwiper() {
    const thumbnailSlide = this.querySelector(".thumbnail-slide");
    if (thumbnailSlide?.swiper) {
      thumbnailSlide.swiper.update();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.boundResponsive);
    this.querySelectorAll("a.media-gallery__image").forEach((anchor) => {
      anchor.removeEventListener("click", this.boundHandleLightboxTrigger);
    });
    this.querySelectorAll(".drift-trigger").forEach((trigger) => {
      trigger.removeEventListener("pointerenter", this.boundHandleDriftIntent);
      trigger.removeEventListener("focusin", this.boundHandleDriftIntent);
    });
    this.destroyLightbox();
    this.destroyDrift();
  }
}

customElements.define("zoom-action", ZoomAction);
